import { drawCoupons } from './coupons';
import { updateFavicon } from './update-favicon';
import { isElementVisible } from './intersection-observer';
import { setServiceOfferings } from './parseServiceOfferings';
import { setMiniCartLoading } from '../templates/components/pdp/utils/utils';
import { CartContextHelper } from './cart/context/CartContext';
import { pageLocation } from './location/pageLocation';
import { trackRemoveFromCartShopify } from './cart/trackRemoveFromCartShopify';
import { transformCartItemForTracking } from './cart/transformCartItemForTracking';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from './shopify/getStoreCurrency';
import { trackViewCartShopify } from './cart/trackViewCartShopify';
import { trackCheckoutTriggered } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackCheckoutTriggered';
import { drawCartServices } from './cart/drawCartServices';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { getCartApiResponse, getBonusItems, splitMismatchedItems, updateCartWithCartApiResponse } from './cartApi';
import { refreshTieredProductDetails } from './cart/refreshTieredProductDetails';
import { ShopifyStorefrontClient } from '../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from './cart/transformStorefrontCartToJbCart';
import { hasReachedMaxQty } from './checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from './checkMaxQty/showMaxQtyAlert';
import { applyLegacyTransformations, getCartQuantities } from './cartTransformations';
import { addToLocalSet, getLocal, removeLocal, LocalStorageKeys } from './localStorage';
import { getMarketplacePropertiesFromExistingItem } from './cart/refreshTieredProductDetails';
import { JbCart } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { fixInvalidCart } from './fixInvalidCart';
import { ProductClient } from '../api/algolia/ProductClient';

window.Jbhifi ||= {} as any;
window.Jbhifi.isProd = process.env.NODE_ENV === 'production';
window.Jbhifi.isProductionEnv = process.env.ENVIRONMENT === 'production';
window.Jbhifi.cartLoading = window.Jbhifi.cartLoading || false;

const storefrontClient = new ShopifyStorefrontClient();
const productClient = new ProductClient();

// Set cart loading
export const setCartLoading = (loading: boolean) => {
  setMiniCartLoading(loading);
  window.Jbhifi.cartLoading = loading;

  const cartContainer = document.querySelector('.cart-container');
  if (cartContainer) {
    if (loading) {
      cartContainer.classList.add('loading');
    } else {
      cartContainer.classList.remove('loading');
    }
  }
};

// is the cart loading?
export const getCartLoading = () => window.Jbhifi.cartLoading;

// Load and draw the cart on page load
export const initCart = async () => {
  setCartLoading(true);
  // Store cart bundle suggestions
  const suggestions = window.Jbhifi?.cartSuggestions;
  const allSuggestedSkus =
    suggestions?.flatMap(suggestion => suggestion.suggestedSkus.map(sku => sku.toString())) || [];
  const searchResults = await productClient.getProductsFromSkus(allSuggestedSkus);
  const suggestedProducts = searchResults?.data?.products ?? [];
  suggestions?.forEach(suggestion => {
    suggestion.suggestedHits = suggestedProducts.filter(hit => suggestion.suggestedSkus.includes(Number(hit.sku)));
  });
  window.Jbhifi.cartSuggestions = suggestions;

  const cart = await storefrontClient.getCart();
  const transformedCart = transformStorefrontCartToJbCart(cart);
  await refreshTieredProductDetails(transformedCart);
  await drawCart(transformedCart);
};

export const drawCart = async (cart: JbCart | undefined) => {
  if (!cart) return;

  // Fix invalid cart (assumed to be from existing legacy cart)
  cart = await fixInvalidCart(cart);

  //CartAPI validation
  let basketApiResponse;
  if (cart?.items.length) {
    basketApiResponse = await getCartApiResponse(cart);
    const bonusCart = await getBonusItems(basketApiResponse);
    const splitCart = await splitMismatchedItems(basketApiResponse, cart);
    let updatedCart = splitCart || bonusCart;

    // If there were mismatched items, or bonus items, we need to refresh the basket response from CartAPI
    if (updatedCart) {
      basketApiResponse = await getCartApiResponse(updatedCart);
      cart = updatedCart;
    }

    // Update cart with services/promotions/etc
    setServiceOfferings(basketApiResponse);
    updatedCart = await updateCartWithCartApiResponse(basketApiResponse, cart);
    cart = updatedCart || cart;
  } else {
    // Reset removed bonus items if cart is empty
    removeLocal(LocalStorageKeys.RemovedBonusSkus);
  }

  // Apply transformations with changes from CartAPI
  applyLegacyTransformations(cart);

  // Save count of each item in cart for max quantity validation, to prevent having to check cart quantity on every add
  window.Jbhifi.cartQuantities = getCartQuantities(cart);

  // Set preferred store if it has changed
  const preferredStore = getLocal(LocalStorageKeys.MySelectedStore, true);
  if (
    window.Jbhifi.localisationSettings.shouldSaveStoreToCheckout &&
    preferredStore?.shopifyId &&
    cart?.preferredStore !== preferredStore.shopifyId
  ) {
    await storefrontClient.setPreferredStore(preferredStore.shopifyId);
  }

  const cartCount = document.getElementById('cart-count');
  if (cartCount) {
    cartCount.textContent = cart.item_count.toString();
    cartCount.className = `count-${cart.item_count}`;
    if (window.localStorage.getItem('cart-counter-animation')) {
      cartCount.setAttribute('data-trigger', 'update');
      setTimeout(() => {
        cartCount.removeAttribute('data-trigger');
      }, 1000);
    }
  }
  window.localStorage.removeItem('cart-counter-animation');

  // Mini cart
  const miniCartContainer = document.querySelector('div[data-cart-mini]');
  if (miniCartContainer) {
    await drawCoupons({ location: pageLocation('mini-cart-other') }, cart, basketApiResponse);
    const minicartTemplate = require('../handlebars/cart/cart-mini.handlebars'); // eslint-disable-line
    miniCartContainer.innerHTML = minicartTemplate(cart);
    drawCartServices();

    // Init lazy images
    window.initLazyImages(miniCartContainer);

    // Mini Cart Checkout button GTM event
    const miniCartCheckoutBtn = document.querySelector('.cart-buttons .cart-buttons-checkout');
    const minicartCartBtn = document.querySelector('.cart-buttons .cart-buttons-cart');
    const removeClick = document.querySelectorAll('.cart-remove');

    if (miniCartCheckoutBtn) {
      miniCartCheckoutBtn.removeEventListener('click', () => {
        const gtmData = {
          event: 'sendEvent',
          eventCategory: 'Enhanced Ecommerce',
          eventAction: 'Checkout from minicart',
        };

        window.dataLayer.push(gtmData);
      });

      miniCartCheckoutBtn.addEventListener('click', () => {
        const gtmData = {
          event: 'sendEvent',
          eventCategory: 'Enhanced Ecommerce',
          eventAction: 'Checkout from minicart',
        };

        window.dataLayer.push(gtmData);

        trackCheckoutTriggered({
          triggerSource: 'mini_cart',
        });
      });

      removeClick.forEach(link => {
        link.addEventListener('click', async () => {
          const { sku, lineId } = (link as HTMLElement).dataset;
          const itemToRemove = cart.items.find(item => item.line_id === lineId);
          setCartLoading(true);

          const updatedCart = await storefrontClient.removeFromCart(lineId ?? '');
          const transformedCart = transformStorefrontCartToJbCart(updatedCart);

          if (itemToRemove?.properties._isBonusItem === 'true') {
            addToLocalSet(LocalStorageKeys.RemovedBonusSkus, sku);
          }

          await drawCart(transformedCart);
          trackRemoveFromCartShopify(itemToRemove);
          trackViewCartShopify(transformedCart);

          //TODO: OLD GA TRACKING TO BE REMOVED
          const decreaseSku = link.previousElementSibling?.getAttribute('data-sku');
          const gtmRemovedProduct = window.gtm.cartProducts?.find((product: any) => product.id === decreaseSku);
          const cartItems = cart.items;
          cartItems.map((item, pos) => {
            if (item.sku === decreaseSku) {
              // to-do: remove old datalayer
              const gtmData = {
                event: 'removeFromCart',
                ecommerce: {
                  currencyCode: getStoreCurrency(),
                  remove: {
                    products: [
                      {
                        sku: item.sku,
                        name: item.product_title,
                        id: item.product_id,
                        price: item.final_line_price,
                        brand: item.vendor,
                        category: item.product_type,
                        quantity: item.quantity,
                        position: pos + 1,
                        dimension3: gtmRemovedProduct?.dimension3,
                        dimension4: gtmRemovedProduct?.dimension4,
                        dimension5: gtmRemovedProduct?.dimension5,
                        dimension6: gtmRemovedProduct?.dimension6,
                        dimension7: gtmRemovedProduct?.dimension7,
                        variants: gtmRemovedProduct?.variants || undefined,
                        metric1: item.final_line_price,
                      },
                    ],
                  },
                },
              };
              window.dataLayer.push(gtmData);
            }
          });
        });
      });

      const incrementItem = document.querySelectorAll('.mini-cart-container .value--increase');
      incrementItem.forEach(link => {
        link.addEventListener('click', async () => {
          const itemQtyElement = link.closest('.item-qty');
          const increaseLineId = itemQtyElement ? itemQtyElement.getAttribute('data-line-id') : null;
          const item = cart.items.find(item => item.line_id === increaseLineId);
          if (item) {
            if (hasReachedMaxQty(item.id)) {
              showMaxQtyAlert();
              return;
            }
            setCartLoading(true);
            const properties = {
              _maxQty: item.properties._maxQty,
              _priceAtc: item.properties._priceAtc,
              _availabilityCanPreOrder: item.properties._availabilityCanPreOrder,
              _availabilityFulfilment: item.properties._availabilityFulfilment,
            };
            const marketplaceProperties = getMarketplacePropertiesFromExistingItem(item);
            const attributes = filterAndMapAttributes({ ...properties, ...marketplaceProperties });
            const updatedCart = await storefrontClient.addToCart(item.id, 1, attributes);

            const transformedCart = transformStorefrontCartToJbCart(updatedCart);
            drawCart(transformedCart);
            trackAddToCart({
              location: 'minicart',
              currency: getStoreCurrency(),
              addedItems: transformCartItemForTracking(item),
            });
            trackViewCartShopify(transformedCart);

            //TODO: OLD GA TRACKING TO BE REMOVED
            const gtmAddedProduct = window.gtm.cartProducts?.find((product: any) => product.id === item.sku);
            const gtmData = {
              event: 'addToCart',
              ecommerce: {
                currencyCode: getStoreCurrency(),
                add: {
                  products: [
                    {
                      sku: item.sku,
                      name: item.product_title,
                      id: item.product_id,
                      price: item.final_line_price,
                      brand: item.vendor,
                      category: item.product_type,
                      quantity: 1,
                      position: item.cartIndex,
                      dimension3: gtmAddedProduct?.dimension3,
                      dimension4: gtmAddedProduct?.dimension4,
                      dimension5: gtmAddedProduct?.dimension5,
                      dimension6: gtmAddedProduct?.dimension6,
                      dimension7: gtmAddedProduct?.dimension7,
                      variants: gtmAddedProduct?.variants || undefined,
                      metric1: item.final_line_price,
                    },
                  ],
                },
              },
            };
            return window.dataLayer.push(gtmData);
          }
        });
      });

      const decrementItem = document.querySelectorAll('.mini-cart-container .value--decrease');
      decrementItem.forEach(link => {
        link.addEventListener('click', async () => {
          const itemQtyElement = link.closest('.item-qty');
          const decreaseLineId = itemQtyElement ? itemQtyElement.getAttribute('data-line-id') : null;
          const item = cart.items.find(item => item.line_id === decreaseLineId);
          if (item) {
            setCartLoading(true);
            const updatedCart = await storefrontClient.updateCartLine(decreaseLineId ?? '', item.quantity - 1);
            const transformedCart = transformStorefrontCartToJbCart(updatedCart);

            await drawCart(transformedCart);
            trackRemoveFromCartShopify(item);
            trackViewCartShopify(transformedCart);

            //TODO: OLD GA TRACKING TO BE REMOVED
            const gtmRemovedProduct = window.gtm.cartProducts?.find((product: any) => product.id === item.sku);
            const gtmData = {
              event: 'removeFromCart',
              ecommerce: {
                currencyCode: getStoreCurrency(),
                remove: {
                  products: [
                    {
                      sku: item.sku,
                      name: item.product_title,
                      id: item.product_id,
                      price: item.final_line_price,
                      brand: item.vendor,
                      category: item.product_type,
                      quantity: 1,
                      position: item.cartIndex,
                      dimension3: gtmRemovedProduct?.dimension3,
                      dimension4: gtmRemovedProduct?.dimension4,
                      dimension5: gtmRemovedProduct?.dimension5,
                      dimension6: gtmRemovedProduct?.dimension6,
                      dimension7: gtmRemovedProduct?.dimension7,
                      variants: gtmRemovedProduct?.variants || undefined,
                      metric1: item.final_line_price,
                    },
                  ],
                },
              },
            };
            return window.dataLayer.push(gtmData);
          }
        });
      });
    }

    if (minicartCartBtn) {
      minicartCartBtn.removeEventListener('click', () => {
        const gtmData = {
          event: 'sendEvent',
          eventCategory: 'Enhanced Ecommerce',
          eventAction: 'View Cart',
        };

        window.dataLayer.push(gtmData);
      });

      minicartCartBtn.addEventListener('click', () => {
        const gtmData = {
          event: 'sendEvent',
          eventCategory: 'Enhanced Ecommerce',
          eventAction: 'View Cart',
        };

        window.dataLayer.push(gtmData);
      });
    }
  }

  // Cart page
  const cartContainer = document.querySelector('div[data-cart-page]');
  if (cartContainer) {
    try {
      // draw line items, coupons and total separately on cart page.
      const lineItemsTemplate = require('../handlebars/cart/line-items.handlebars'); // eslint-disable-line
      const cartTotalTemplate = require('../handlebars/cart/total.handlebars'); // eslint-disable-line
      const cartFixedBarTemplate = require('../handlebars/cart/cart-fixed-bar.handlebars'); // eslint-disable-line
      const lineItemsContainer = document.querySelector('div[data-line-items]');
      if (lineItemsContainer) {
        lineItemsContainer.innerHTML = lineItemsTemplate(cart);
      }
      drawCartServices();
      await drawCoupons({ location: 'cart' }, cart, basketApiResponse);
      const cartTotalElement = document.querySelector('div[data-cart-total]');
      if (cartTotalElement) {
        cartTotalElement.innerHTML = cartTotalTemplate(cart);
      }

      /**
       * Check if the on page cart total is in view. This determines whether we show the bar by default
       * when handlebars re-renders.
       */
      const showFixedBarOnRender = !isElementVisible(document.querySelector('[data-cart-total]'));

      const cartFixedBarElement = document.querySelector('div[data-cart-fixed-bar]');
      if (cartFixedBarElement) {
        cartFixedBarElement.innerHTML = cartFixedBarTemplate({ ...cart, showFixedBarOnRender });
      }
    } catch (error) {
      console.log(error);
    }
    window.Jbhifi.noCartScroll = true;

    const removeLinks = document.querySelectorAll('.remove-link');

    window.initLazyImages(cartContainer);

    removeLinks.forEach(link => {
      link.addEventListener('click', async () => {
        const { sku, lineId } = (link as HTMLElement).dataset;
        const itemToRemove = cart.items.find(item => item.line_id === lineId);

        setCartLoading(true);

        const updatedCart = await storefrontClient.removeFromCart(lineId ?? '');
        const transformedCart = transformStorefrontCartToJbCart(updatedCart);

        if (itemToRemove?.properties._isBonusItem === 'true') {
          addToLocalSet(LocalStorageKeys.RemovedBonusSkus, sku);
        }

        await drawCart(transformedCart);
        trackRemoveFromCartShopify(itemToRemove);
        trackViewCartShopify(transformedCart);
      });
    });

    const quantitySelectors = document.querySelectorAll('select[name="quantity"]');
    quantitySelectors.forEach(selector => {
      selector.addEventListener('change', async e => {
        const element = e.target as HTMLSelectElement;
        const { lineId, variantId, quantity } = element.dataset;
        const item = cart.items.find(item => item.line_id === lineId);
        const prevQuantity = Number(quantity);
        const newQuantity = Number(element.value);
        const quantityIncrease = newQuantity - prevQuantity;
        if (hasReachedMaxQty(item?.id, quantityIncrease)) {
          showMaxQtyAlert();
          element.value = prevQuantity.toString();
          return;
        }
        if (!item) return;
        setCartLoading(true);
        let updatedCart;
        if (prevQuantity === 1) {
          const properties = {
            _maxQty: item.properties._maxQty,
            _priceAtc: item.properties._priceAtc,
            _availabilityCanPreOrder: item.properties._availabilityCanPreOrder,
            _availabilityFulfilment: item.properties._availabilityFulfilment,
          };
          const marketplaceProperties = getMarketplacePropertiesFromExistingItem(item);
          updatedCart = await storefrontClient.addMultipleToCart(
            Array.from({ length: quantityIncrease }, () => ({
              variantId,
              attributes: filterAndMapAttributes({ ...properties, ...marketplaceProperties }),
            }))
          );
        } else {
          updatedCart = await storefrontClient.updateCartLine(lineId ?? '', newQuantity);
        }
        const transformedCart = transformStorefrontCartToJbCart(updatedCart);
        await drawCart(transformedCart);
        if (quantityIncrease > 0) {
          trackAddToCart({
            location: 'cart',
            currency: getStoreCurrency(),
            addedItems: transformCartItemForTracking(item),
          });
        } else if (quantityIncrease < 0) {
          trackRemoveFromCartShopify(item);
        }
        trackViewCartShopify(transformedCart);
      });
    });
  }

  /**
   * Close the cart
   */
  if (cart.item_count === 0) {
    setTimeout(() => {
      hideCart();
    }, 500);
  }

  updateFavicon(cart.item_count);
  CartContextHelper.broadcastCartUpdate(cart);
  setCartLoading(false);
  return basketApiResponse;
};

/**
 * Show cart.
 */
export const showCart = () => {
  const miniCartContainer = document.querySelector('.mini-cart-container');
  if (miniCartContainer) {
    miniCartContainer.classList.add('is-visible');

    // If search results is open in mobile and AddToCart is clicked,
    // close search results when minicart opens
    const body = document.body;
    const searchOpen = body.classList.contains('quicksearch-active');
    if (window.innerWidth < 1000 && searchOpen) {
      const searchCloseInput = document.getElementById('search-close-input');
      if (searchCloseInput) {
        searchCloseInput.click();
      }
    }

    body.classList.add('minicart-open');

    const minicartToggle = document.getElementById('minicart-toggle');
    if (minicartToggle) {
      minicartToggle.parentElement?.classList.add('active');
    }
  }
};

/**
 * Hide cart.
 */
export const hideCart = () => {
  const miniCartContainer = document.querySelector('.mini-cart-container');
  const minicartToggleParent = document.querySelector('#minicart-toggle')?.parentElement;
  const body = document.body;

  if (miniCartContainer) {
    miniCartContainer.classList.remove('is-visible');
  }

  if (minicartToggleParent) {
    minicartToggleParent.classList.remove('active');
  }

  body.classList.remove('minicart-open');
};
